import { Box, Button, Stack, Switch } from '@ff-it/ui';
import { type ReactElement, useId, useState } from 'react';
import { usePermissionContext } from './context';
import type { SectionModel, SectionType } from './types';

function Model({ title, perms }: SectionModel): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const permissionsId = useId();

  const { loading, permissions: enabledPermissions, toggle } = usePermissionContext();

  return (
    <Box>
      <Button
        active={isOpen}
        variant="secondary"
        iconAfter={`chevron-${isOpen ? 'up' : 'down'}` as any}
        type="button"
        aria-controls={permissionsId}
        aria-expanded={isOpen}
        style={{
          width: '100%',
          justifyContent: 'space-between',
        }}
        onClick={() => {
          setIsOpen((o) => !o);
        }}
      >
        {title}
      </Button>
      {isOpen && (
        <Stack
          id={permissionsId}
          as="ul"
          space="0"
          mt="1"
          m="0"
          p="0"
          border="subtle"
          borderRadius="xs"
          overflow="hidden"
          style={{ listStyle: 'none' }}
        >
          {perms.map(({ id, name, description }) => {
            const enabled = Boolean(enabledPermissions[id]);
            return (
              <Box as="li" key={id}>
                <Box
                  as="label"
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  mb="0"
                  borderBottom="subtle"
                  px="3"
                  py="2"
                  style={{ cursor: loading ? 'default' : 'pointer' }}
                >
                  <Box ml="2" mr="auto">
                    <Box>{name}</Box>
                    {/* biome-ignore lint/security/noDangerouslySetInnerHtml: should be safe */}
                    <Box color="gray500" fontSize="xs" dangerouslySetInnerHTML={{ __html: description }} />
                  </Box>
                  <Switch disabled={loading} checked={enabled} onChange={() => toggle(id, !enabled)} />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export function Section({ title, models }: SectionType): ReactElement {
  return (
    <Stack mb="3" space="3">
      <Box as="h4" m="0">
        {title}
      </Box>
      <Stack space="3">
        {models.map((model) => (
          <Model {...model} key={model.model_key} />
        ))}
      </Stack>
    </Stack>
  );
}
